/** @fileoverview ClubOS Executive Dashboard — President & CEO / Board-level strategic view */
import { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftProgress from "components/SoftProgress";
import SoftButton from "components/SoftButton";
import ClickableCard from "components/ClickableCard";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import GradientLineChart from "examples/Charts/LineCharts/GradientLineChart";
import DefaultDoughnutChart from "examples/Charts/DoughnutCharts/DefaultDoughnutChart";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import { financialSummary, alerts, squad, contracts, matches } from "data/mockData";
import { formatCompact, CLUB_NAME, SEASON } from "utils/indiaLocale";

// ─── DERIVED DATA ─────────────────────────────────────────────────────────────
const islMatches = matches.filter((m) => m.outcome !== null && m.competition === "Indian Super League (ISL)");
const islRecord = islMatches.reduce(
  (acc, m) => {
    acc[m.outcome]++;
    acc.gf += m.venue === "H" ? Number((m.score || "0-0").split("-")[0]) : Number((m.score || "0-0").split("-")[1]);
    acc.ga += m.venue === "H" ? Number((m.score || "0-0").split("-")[1]) : Number((m.score || "0-0").split("-")[0]);
    return acc;
  },
  { W: 0, D: 0, L: 0, gf: 0, ga: 0 }
);
const islPoints = islRecord.W * 3 + islRecord.D;
const ffpPct = Math.round((financialSummary.ffpUsed / financialSummary.ffpAllowable) * 100);

const revenueDonutChart = {
  labels: financialSummary.revenueBreakdown.map((r) => r.label),
  datasets: { label: "Revenue (₹)", backgroundColors: ["info", "primary", "success", "warning", "error"], data: financialSummary.revenueBreakdown.map((r) => Math.round(r.value / 1_00_000)) },
};
const budgetVsActualChart = {
  labels: financialSummary.budgetVsActual.map((b) => b.month),
  datasets: [
    { label: "Budget", color: "info",  data: financialSummary.budgetVsActual.map((b) => Math.round(b.budget / 1_00_000)) },
    { label: "Actual", color: "error", data: financialSummary.budgetVsActual.map((b) => Math.round(b.actual / 1_00_000)) },
  ],
};
const overBudgetMonths = financialSummary.budgetVsActual.filter((b) => b.actual > b.budget).map((b) => b.month);
const allRisk = contracts.filter((c) => ["Expiring", "In Negotiations"].includes(c.status));
const contractRisk = allRisk.slice(0, 4);
const priorityAlerts = alerts.filter((a) => !a.read);
const foreign = squad.filter((p) => p.nationality !== "Indian").length;
const DONUT_COLORS = ["#17c1e8", "#cb0c9f", "#82d616", "#fb8c00", "#ea0606"];
const alertIconColor = (type) => ({ error: "error", warning: "warning", info: "info" }[type] || "info");

// ─── DETAIL CONTENT ───────────────────────────────────────────────────────────
const revenueDetail = (
  <SoftBox>
    {financialSummary.revenueBreakdown.map((r, i) => (
      <SoftBox key={r.label} display="flex" justifyContent="space-between" py={0.75} borderBottom={i < financialSummary.revenueBreakdown.length - 1 ? "1px solid #f0f0f0" : "none"}>
        <SoftBox display="flex" alignItems="center" gap={1}>
          <SoftBox width="10px" height="10px" borderRadius="50%" sx={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length], flexShrink: 0 }} />
          <SoftTypography variant="button" color="text">{r.label}</SoftTypography>
        </SoftBox>
        <SoftTypography variant="button" fontWeight="bold">{formatCompact(r.value)}</SoftTypography>
      </SoftBox>
    ))}
  </SoftBox>
);
const budgetDetail = (
  <SoftBox>
    {financialSummary.budgetVsActual.map((b, i) => (
      <SoftBox key={b.month} display="flex" justifyContent="space-between" py={0.75} px={0.5} borderRadius="md"
        borderBottom={i < financialSummary.budgetVsActual.length - 1 ? "1px solid #f0f0f0" : "none"}
        sx={{ backgroundColor: b.actual > b.budget ? "#fff3e0" : "transparent" }}>
        <SoftTypography variant="caption" color="text">{b.month}</SoftTypography>
        <SoftBox display="flex" gap={3}>
          <SoftTypography variant="caption" color="info">B: {Math.round(b.budget / 1_00_000)}L</SoftTypography>
          <SoftTypography variant="caption" color={b.actual > b.budget ? "error" : "success"}>A: {Math.round(b.actual / 1_00_000)}L</SoftTypography>
        </SoftBox>
      </SoftBox>
    ))}
  </SoftBox>
);
const contractDetail = (
  <SoftBox>
    {allRisk.length === 0 && <SoftTypography variant="caption" color="text">No contracts at risk.</SoftTypography>}
    {allRisk.map((c, i) => {
      const daysLeft = Math.max(0, Math.ceil((new Date(c.contractEnd) - new Date()) / (1000 * 60 * 60 * 24)));
      return (
        <SoftBox key={c.id} py={1} borderBottom={i < allRisk.length - 1 ? "1px solid #f0f0f0" : "none"}>
          <SoftBox display="flex" justifyContent="space-between" alignItems="center">
            <SoftBox>
              <SoftTypography variant="button" fontWeight="medium">{c.player}</SoftTypography>
              <SoftTypography variant="caption" color="text" display="block">{c.position} · expires {c.contractEnd} ({daysLeft}d)</SoftTypography>
            </SoftBox>
            <SoftBadge badgeContent={c.status} color={c.status === "Expiring" ? "error" : "warning"} size="xs" container />
          </SoftBox>
        </SoftBox>
      );
    })}
  </SoftBox>
);
const seasonDetail = (
  <SoftBox>
    {islMatches.map((m, i) => (
      <SoftBox key={m.id} display="flex" justifyContent="space-between" py={0.75} borderBottom={i < islMatches.length - 1 ? "1px solid #f0f0f0" : "none"}>
        <SoftBox display="flex" alignItems="center" gap={1}>
          <SoftBox width="1.5rem" height="1.5rem" borderRadius="sm" display="flex" alignItems="center" justifyContent="center"
            sx={{ backgroundColor: m.outcome === "W" ? "#82d616" : m.outcome === "D" ? "#fb8c00" : "#ea0606" }}>
            <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "white" }}>{m.outcome}</SoftTypography>
          </SoftBox>
          <SoftTypography variant="caption" color="text">{m.venue === "H" ? "vs" : "@"} {m.venue === "H" ? m.away : m.home}</SoftTypography>
        </SoftBox>
        <SoftTypography variant="caption" fontWeight="medium">{m.score}</SoftTypography>
      </SoftBox>
    ))}
  </SoftBox>
);
const alertsDetail = (
  <SoftBox>
    {priorityAlerts.map((alert, i) => (
      <SoftBox key={alert.id} py={1.25} borderBottom={i < priorityAlerts.length - 1 ? "1px solid #f0f0f0" : "none"}>
        <SoftBox display="flex" alignItems="flex-start" gap={1.5}>
          <SoftBox bgColor={alertIconColor(alert.type)} variant="gradient" width="2rem" height="2rem" borderRadius="md"
            display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
            <Icon sx={{ color: "white", fontSize: "0.9rem" }}>
              {alert.type === "error" ? "error" : alert.type === "warning" ? "warning" : "info"}
            </Icon>
          </SoftBox>
          <SoftBox flex={1}>
            <SoftTypography variant="button" fontWeight="medium" display="block">{alert.title}</SoftTypography>
            <SoftTypography variant="caption" color="text">{alert.description}</SoftTypography>
            <SoftBox display="flex" justifyContent="space-between" mt={0.5}>
              <SoftTypography variant="caption" color="secondary">{alert.time}</SoftTypography>
              <SoftBadge badgeContent={alert.category} color="secondary" size="xs" container />
            </SoftBox>
          </SoftBox>
        </SoftBox>
      </SoftBox>
    ))}
  </SoftBox>
);
// ─── COMPONENT ────────────────────────────────────────────────────────────────
function ExecutiveDashboard() {
  const budgetRemaining = financialSummary.transferBudget - financialSummary.transferBudgetUsed;
  const ffpHeadroom = financialSummary.ffpAllowable - financialSummary.ffpUsed;
  const [kpiModal, setKpiModal] = useState(null);

  const kpiHoverSx = { cursor: "pointer", borderRadius: "lg", transition: "transform 0.18s, box-shadow 0.18s", "&:hover": { transform: "translateY(-3px)", boxShadow: "0 10px 28px rgba(0,0,0,0.13)" } };

  const kpiConfigs = {
    revenue:  { title: "Annual Revenue",   body: <SoftBox><SoftTypography variant="h4" fontWeight="bold" color="info" mb={1}>₹82 Cr</SoftTypography><SoftTypography variant="body2" color="text" mb={1.5}>Broadcasting + Sponsorship breakdown:</SoftTypography>{revenueDetail}</SoftBox> },
    salary:   { title: "Monthly Cost",     body: <SoftBox><SoftTypography variant="h4" fontWeight="bold" color="error" mb={1}>{formatCompact(financialSummary.totalMonthlySalaryBill)}</SoftTypography><SoftTypography variant="body2" color="text">Total squad salary bill. Up +2.4% vs last month.</SoftTypography></SoftBox> },
    transfer: { title: "Transfer Budget",  body: <SoftBox><SoftTypography variant="h4" fontWeight="bold" color="success" mb={1}>{formatCompact(budgetRemaining)}</SoftTypography><SoftTypography variant="body2" color="text">Remaining of {formatCompact(financialSummary.transferBudget)} total. Used: {formatCompact(financialSummary.transferBudgetUsed)}.</SoftTypography></SoftBox> },
    ffp:      { title: "FFP Headroom",     body: <SoftBox><SoftTypography variant="h4" fontWeight="bold" color="warning" mb={1}>{formatCompact(ffpHeadroom)}</SoftTypography><SoftTypography variant="body2" color="text" mb={1.5}>{ffpPct}% of allowable limit used.</SoftTypography><SoftProgress value={ffpPct} color={ffpPct > 85 ? "error" : ffpPct > 65 ? "warning" : "success"} /><SoftBox display="flex" justifyContent="space-between" mt={0.75}><SoftTypography variant="caption" color="text">Used: {formatCompact(financialSummary.ffpUsed)}</SoftTypography><SoftTypography variant="caption" color="text">Limit: {formatCompact(financialSummary.ffpAllowable)}</SoftTypography></SoftBox></SoftBox> },
  };
  const activeKpi = kpiModal ? kpiConfigs[kpiModal] : null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>

        {/* ── Page heading ── */}
        <SoftBox mb={3} display="flex" justifyContent="space-between" alignItems="flex-end">
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">Executive Dashboard</SoftTypography>
            <SoftTypography variant="body2" color="text">{CLUB_NAME} &nbsp;·&nbsp; President &amp; CEO View &nbsp;·&nbsp; Season {SEASON}</SoftTypography>
          </SoftBox>
          <SoftBox display="flex" gap={1} alignItems="center">
            {[{ label: `${islRecord.W}W`, color: "#82d616" }, { label: `${islRecord.D}D`, color: "#fb8c00" }, { label: `${islRecord.L}L`, color: "#ea0606" }, { label: `${islPoints} pts`, color: "#17c1e8" }].map(({ label, color }) => (
              <SoftBox key={label} px={1.5} py={0.5} borderRadius="md" sx={{ backgroundColor: "#f8f9fa", border: "1px solid #e9ecef" }}>
                <SoftTypography variant="caption" fontWeight="bold" sx={{ color }}>{label}</SoftTypography>
              </SoftBox>
            ))}
          </SoftBox>
        </SoftBox>

        {/* ── KPI row ── */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            {[
              { key: "revenue", title: "Annual Revenue",   count: "₹82 Cr",                                 pct: { color: "info",    text: "All Income" }, icon: { color: "info",    component: "account_balance" } },
              { key: "salary",  title: "Monthly Cost",     count: formatCompact(financialSummary.totalMonthlySalaryBill), pct: { color: "error",   text: "+2.4% vs last month" },       icon: { color: "error",   component: "payments" } },
              { key: "transfer",title: "Transfer Budget",  count: formatCompact(budgetRemaining),            pct: { color: "success", text: `of ${formatCompact(financialSummary.transferBudget)} total` }, icon: { color: "success", component: "swap_horiz" } },
              { key: "ffp",     title: "FFP Headroom",     count: formatCompact(ffpHeadroom),                pct: { color: "warning", text: `${ffpPct}% of limit used` },      icon: { color: "warning", component: "policy" } },
            ].map(({ key, title, count, pct, icon }) => (
              <Grid item xs={12} sm={6} xl={3} key={key}>
                <SoftBox onClick={() => setKpiModal(key)} sx={kpiHoverSx}>
                  <MiniStatisticsCard title={{ text: title, fontWeight: "medium" }} count={count} percentage={pct} icon={icon} />
                </SoftBox>
              </Grid>
            ))}
          </Grid>
        </SoftBox>

        {/* ── Revenue Donut + Budget vs Actual ── */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={5}>
              <ClickableCard title="Revenue Breakdown" icon="pie_chart" iconColor="info" tooltip="Click to see full revenue data table" detailContent={revenueDetail}>
                <Card sx={{ height: "100%" }}>
                  <SoftBox p={2} pb={0}>
                    <SoftTypography variant="h6" fontWeight="medium">Revenue Breakdown</SoftTypography>
                    <SoftTypography variant="caption" color="text">Annual revenue by stream (₹ Lakhs)</SoftTypography>
                  </SoftBox>
                  <DefaultDoughnutChart title="" description="" height="13rem" chart={revenueDonutChart} />
                  <SoftBox px={2} pb={2}>
                    {financialSummary.revenueBreakdown.map((r, i) => (
                      <SoftBox key={r.label} display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <SoftBox display="flex" alignItems="center" gap={0.75}>
                          <SoftBox width="8px" height="8px" borderRadius="50%" sx={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length], flexShrink: 0 }} />
                          <SoftTypography variant="caption" color="text">{r.label}</SoftTypography>
                        </SoftBox>
                        <SoftTypography variant="caption" fontWeight="medium">{formatCompact(r.value)}</SoftTypography>
                      </SoftBox>
                    ))}
                  </SoftBox>
                </Card>
              </ClickableCard>
            </Grid>
            <Grid item xs={12} lg={7}>
              <ClickableCard title="Budget vs Actual Spend" icon="trending_up" iconColor="error" tooltip="Click to see monthly budget vs actual table" detailContent={budgetDetail}>
                <Card sx={{ height: "100%" }}>
                  <SoftBox p={2} pb={0}>
                    <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start">
                      <SoftBox>
                        <SoftTypography variant="h6" fontWeight="medium">Budget vs Actual Spend</SoftTypography>
                        <SoftTypography variant="caption" color="text">Monthly spend vs budget (₹ Lakhs)</SoftTypography>
                      </SoftBox>
                      <SoftBox display="flex" gap={1.5} alignItems="center" mt={0.5}>
                        <SoftBox display="flex" alignItems="center" gap={0.5}><SoftBox width="12px" height="3px" borderRadius="3px" sx={{ backgroundColor: "#17c1e8" }} /><SoftTypography variant="caption" color="text">Budget</SoftTypography></SoftBox>
                        <SoftBox display="flex" alignItems="center" gap={0.5}><SoftBox width="12px" height="3px" borderRadius="3px" sx={{ backgroundColor: "#ea0606" }} /><SoftTypography variant="caption" color="text">Actual</SoftTypography></SoftBox>
                      </SoftBox>
                    </SoftBox>
                  </SoftBox>
                  <GradientLineChart title="" description="" height="14rem" chart={budgetVsActualChart} />
                  {overBudgetMonths.length > 0 && (
                    <SoftBox px={2} pb={2}>
                      <SoftBox display="flex" alignItems="center" gap={0.75} p={1} borderRadius="md" sx={{ backgroundColor: "#fff3e0", border: "1px solid #ffcc80" }}>
                        <Icon sx={{ color: "#fb8c00", fontSize: "1rem" }}>warning</Icon>
                        <SoftTypography variant="caption" color="warning">Over budget in: {overBudgetMonths.join(", ")}</SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  )}
                </Card>
              </ClickableCard>
            </Grid>
          </Grid>
        </SoftBox>

        {/* ── Club Metrics + Contract Risk + Season Performance ── */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={4}>
              <Card sx={{ height: "100%" }}>
                <SoftBox p={2}>
                  <SoftTypography variant="h6" fontWeight="medium" mb={2}>Club Metrics</SoftTypography>
                  <Grid container spacing={1.5}>
                    {[
                      { label: "Squad Size",    value: squad.length,     color: "info" },
                      { label: "Foreign Slots", value: `${foreign}/6`,  color: foreign >= 6 ? "error" : "success" },
                      { label: "ISL Position",  value: `#${Math.ceil((20 - islPoints) / 3) < 1 ? 1 : "–"}`, color: "dark" },
                      { label: "GF − GA",       value: `${islRecord.gf}−${islRecord.ga} (${islRecord.gf - islRecord.ga >= 0 ? "+" : ""}${islRecord.gf - islRecord.ga})`, color: islRecord.gf - islRecord.ga >= 0 ? "success" : "error" },
                    ].map(({ label, value, color }) => (
                      <Grid item xs={6} key={label}>
                        <SoftBox p={1.5} borderRadius="lg" sx={{ backgroundColor: "#f8f9fa", textAlign: "center" }}>
                          <SoftTypography variant="h5" fontWeight="bold" color={color}>{value}</SoftTypography>
                          <SoftTypography variant="caption" color="text">{label}</SoftTypography>
                        </SoftBox>
                      </Grid>
                    ))}
                  </Grid>
                  <Divider sx={{ my: 1.5 }} />
                  <SoftBox display="flex" justifyContent="space-between">
                    <SoftTypography variant="caption" color="text">ISL Points</SoftTypography>
                    <SoftTypography variant="button" fontWeight="bold" color="info">{islPoints} pts</SoftTypography>
                  </SoftBox>
                  <SoftBox display="flex" justifyContent="space-between" mt={0.5}>
                    <SoftTypography variant="caption" color="text">Record</SoftTypography>
                    <SoftTypography variant="caption" fontWeight="medium" color="dark">{islRecord.W}W / {islRecord.D}D / {islRecord.L}L</SoftTypography>
                  </SoftBox>
                </SoftBox>
              </Card>
            </Grid>

            <Grid item xs={12} lg={4}>
              <ClickableCard title="Contract Risk" icon="gavel" iconColor="warning" tooltip="Click to see all at-risk contracts" detailContent={contractDetail}>
                <Card sx={{ height: "100%" }}>
                  <SoftBox p={2}>
                    <SoftTypography variant="h6" fontWeight="medium" mb={1.5}>Contract Risk</SoftTypography>
                    {contractRisk.length === 0 && <SoftTypography variant="caption" color="text">No contracts at risk.</SoftTypography>}
                    {contractRisk.map((c, i) => {
                      const daysLeft = Math.max(0, Math.ceil((new Date(c.contractEnd) - new Date()) / (1000 * 60 * 60 * 24)));
                      return (
                        <SoftBox key={c.id} mb={i < contractRisk.length - 1 ? 1.5 : 0}>
                          <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                            <SoftBox>
                              <SoftTypography variant="button" fontWeight="medium">{c.player}</SoftTypography>
                              <SoftTypography variant="caption" color="text" display="block">{c.position}</SoftTypography>
                            </SoftBox>
                            <SoftBox textAlign="right">
                              <SoftBadge badgeContent={c.status} color={c.status === "Expiring" ? "error" : "warning"} size="xs" container />
                              <SoftTypography variant="caption" color="text" display="block" mt={0.25}>{daysLeft}d left</SoftTypography>
                            </SoftBox>
                          </SoftBox>
                          {i < contractRisk.length - 1 && <Divider sx={{ mt: 1 }} />}
                        </SoftBox>
                      );
                    })}
                  </SoftBox>
                </Card>
              </ClickableCard>
            </Grid>

            <Grid item xs={12} lg={4}>
              <ClickableCard title="Season Performance" icon="sports_soccer" iconColor="success" tooltip="Click to see all ISL results" detailContent={seasonDetail}>
                <Card sx={{ height: "100%" }}>
                  <SoftBox p={2}>
                    <SoftTypography variant="h6" fontWeight="medium" mb={1.5}>Season Performance</SoftTypography>
                    <SoftTypography variant="caption" color="text" fontWeight="bold" display="block" mb={0.75}>LAST 5 RESULTS</SoftTypography>
                    <SoftBox display="flex" gap={0.75} mb={2}>
                      {islMatches.slice(-5).map((m) => (
                        <Tooltip key={m.id} title={`${m.venue === "H" ? "vs" : "@"} ${m.venue === "H" ? m.away : m.home} · ${m.score}`} placement="top">
                          <SoftBox width="2rem" height="2rem" borderRadius="md" display="flex" alignItems="center" justifyContent="center"
                            sx={{ backgroundColor: m.outcome === "W" ? "#82d616" : m.outcome === "D" ? "#fb8c00" : "#ea0606", cursor: "default" }}>
                            <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "white" }}>{m.outcome}</SoftTypography>
                          </SoftBox>
                        </Tooltip>
                      ))}
                    </SoftBox>
                    <Divider sx={{ my: 1 }} />
                    <SoftTypography variant="caption" color="text" fontWeight="bold" display="block" mb={1}>W / D / L BREAKDOWN</SoftTypography>
                    {[
                      { label: "Wins",   count: islRecord.W, total: islMatches.length, color: "success" },
                      { label: "Draws",  count: islRecord.D, total: islMatches.length, color: "warning" },
                      { label: "Losses", count: islRecord.L, total: islMatches.length, color: "error"   },
                    ].map(({ label, count, total, color }) => {
                      const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                      return (
                        <SoftBox key={label} mb={1}>
                          <SoftBox display="flex" justifyContent="space-between" mb={0.25}>
                            <SoftTypography variant="caption" color="text">{label}</SoftTypography>
                            <SoftTypography variant="caption" fontWeight="medium">{count} ({pct}%)</SoftTypography>
                          </SoftBox>
                          <SoftProgress value={pct} color={color} />
                        </SoftBox>
                      );
                    })}
                  </SoftBox>
                </Card>
              </ClickableCard>
            </Grid>
          </Grid>
        </SoftBox>

        {/* ── Priority Alerts ── */}
        <ClickableCard title="Priority Alerts" icon="notifications_active" iconColor="error" tooltip="Click to see all unread alerts with full descriptions" detailContent={alertsDetail}>
          <Card>
            <SoftBox p={2}>
              <SoftBox display="flex" alignItems="center" gap={1.5} mb={2}>
                <SoftTypography variant="h6" fontWeight="medium">Priority Alerts</SoftTypography>
                <SoftBadge badgeContent={priorityAlerts.length} color="error" size="xs" circular container />
              </SoftBox>
              {priorityAlerts.map((alert, i) => (
                <SoftBox key={alert.id}>
                  <SoftBox display="flex" alignItems="flex-start" py={1}>
                    <SoftBox bgColor={alertIconColor(alert.type)} variant="gradient" width="2.2rem" height="2.2rem" borderRadius="md"
                      display="flex" alignItems="center" justifyContent="center" mr={1.5} flexShrink={0}>
                      <Icon sx={{ color: "white", fontSize: "1rem" }}>
                        {alert.type === "error" ? "error" : alert.type === "warning" ? "warning" : "info"}
                      </Icon>
                    </SoftBox>
                    <SoftBox flex={1}>
                      <SoftTypography variant="button" fontWeight="medium" display="block">{alert.title}</SoftTypography>
                      <SoftTypography variant="caption" color="text">
                        {alert.description.length > 80 ? `${alert.description.substring(0, 80)}…` : alert.description}
                      </SoftTypography>
                      <SoftBox display="flex" justifyContent="space-between" alignItems="center" mt={0.25}>
                        <SoftTypography variant="caption" color="secondary">{alert.time}</SoftTypography>
                        <SoftBadge badgeContent={alert.category} color="secondary" size="xs" container />
                      </SoftBox>
                    </SoftBox>
                  </SoftBox>
                  {i < priorityAlerts.length - 1 && <Divider sx={{ my: 0 }} />}
                </SoftBox>
              ))}
            </SoftBox>
          </Card>
        </ClickableCard>

        {/* ── KPI Dialog ── */}
        <Dialog open={Boolean(kpiModal)} onClose={() => setKpiModal(null)} maxWidth="xs" fullWidth>
          {activeKpi && (
            <>
              <DialogTitle sx={{ pb: 1 }}><SoftTypography variant="h6" fontWeight="bold" component="span">{activeKpi.title}</SoftTypography></DialogTitle>
              <DialogContent>{activeKpi.body}</DialogContent>
              <DialogActions>
                <SoftButton variant="gradient" color="dark" size="small" onClick={() => setKpiModal(null)}>Close</SoftButton>
              </DialogActions>
            </>
          )}
        </Dialog>

      </SoftBox>
      <Footer company={{ href: "#", name: CLUB_NAME }} links={[]} />
    </DashboardLayout>
  );
}

export default ExecutiveDashboard;
